import React, { useState } from "react";
import { ApiBookmark } from "../lib/api-client";
import { formatTimestamp, parseTimestamp } from "../lib/timestamp";

interface Props {
  bookmark: ApiBookmark;
  onSeek: (seconds: number) => void;
  onEdit: (patch: { label?: string; timestampSec?: number }) => Promise<void>;
  onDelete: () => Promise<void>;
}

export default function BookmarkRow({
  bookmark,
  onSeek,
  onEdit,
  onDelete,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [labelDraft, setLabelDraft] = useState(bookmark.label);
  const [timestampDraft, setTimestampDraft] = useState(
    formatTimestamp(bookmark.timestampSec),
  );
  const [rowError, setRowError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function startEdit() {
    setLabelDraft(bookmark.label);
    setTimestampDraft(formatTimestamp(bookmark.timestampSec));
    setRowError(null);
    setEditing(true);
  }

  async function save() {
    const nextTs = parseTimestamp(timestampDraft);
    if (nextTs === null) {
      setRowError("Use mm:ss, hh:mm:ss, or a number of seconds.");
      return;
    }
    setBusy(true);
    setRowError(null);
    try {
      await onEdit({ label: labelDraft.trim(), timestampSec: nextTs });
      setEditing(false);
    } catch (err) {
      setRowError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setRowError(null);
    try {
      await onDelete();
    } catch (err) {
      setRowError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <li className="border border-gray-700 bg-gray-950 rounded p-2 space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={timestampDraft}
            onChange={(e) => setTimestampDraft(e.target.value)}
            className="w-24 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-xs text-white font-mono focus:outline-none focus:border-orange-500"
          />
          <input
            type="text"
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            maxLength={120}
            className="flex-1 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-orange-500"
          />
        </div>
        <div className="flex gap-2 justify-end text-xs">
          <button
            type="button"
            disabled={busy}
            onClick={() => setEditing(false)}
            className="px-2 py-1 text-gray-400 hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void save()}
            className="px-2.5 py-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 text-white rounded"
          >
            Save
          </button>
        </div>
        {rowError && <p className="text-xs text-red-400">{rowError}</p>}
      </li>
    );
  }

  return (
    <li
      className="group flex items-center gap-3 rounded px-2 py-1.5 hover:bg-gray-900 transition"
      title={bookmark.label}
    >
      <button
        type="button"
        onClick={() => onSeek(bookmark.timestampSec)}
        className="font-mono text-xs text-orange-300 hover:text-orange-200 transition shrink-0 tabular-nums"
        title="Seek to this moment"
      >
        ▶ {formatTimestamp(bookmark.timestampSec)}
      </button>
      <span className="flex-1 text-sm text-gray-200 truncate">
        {bookmark.label || (
          <span className="font-display-italic text-gray-600">no note</span>
        )}
      </span>

      <div className="flex items-center gap-3 text-xs opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
        <button
          type="button"
          onClick={startEdit}
          disabled={busy}
          className="text-gray-500 hover:text-gray-300"
          aria-label="Edit bookmark"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => void remove()}
          disabled={busy}
          className="text-gray-500 hover:text-red-400"
          aria-label="Delete bookmark"
        >
          Remove
        </button>
      </div>
      {rowError && <span className="text-xs text-red-400">{rowError}</span>}
    </li>
  );
}
